import React from 'react';
import { Receipt, DollarSign, RefreshCw, AlertTriangle, Eye, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';

const SummaryCard = ({ icon: Icon, iconBg, iconColor, label, value, valueColor, sub, ring }) => (
  <Card className={ring ? 'ring-2 ring-amber-400' : ''}>
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 ${iconBg} rounded-lg`}><Icon className={`h-5 w-5 ${iconColor}`} /></div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className={`text-2xl font-bold ${valueColor || ''}`}>{value}</p>
          {sub && <p className="text-xs text-gray-500">{sub}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

export const CreditMemosTab = ({ loadingCreditMemos, creditMemoSummary, creditMemos, loadCreditMemos, onViewCreditMemo }) => {
  if (loadingCreditMemos) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const s = creditMemoSummary?.summary;

  return (
    <>
      {creditMemoSummary && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <SummaryCard icon={Receipt} iconBg="bg-blue-100" iconColor="text-blue-600" label="Total Credit Memos" value={s?.total_count || 0} />
          <SummaryCard icon={DollarSign} iconBg="bg-red-100" iconColor="text-red-600" label="Total Credits" value={`$${(s?.total_credit_amount || 0).toFixed(2)}`} valueColor="text-red-600" />
          <SummaryCard
            icon={RefreshCw} iconBg="bg-amber-100" iconColor="text-amber-600" label="Pending Refunds"
            value={s?.pending_refunds_count || 0} valueColor="text-amber-600"
            sub={s?.pending_refunds_amount > 0 ? `$${s.pending_refunds_amount.toFixed(2)}` : null}
            ring={s?.pending_refunds_count > 0}
          />
          <SummaryCard icon={AlertTriangle} iconBg="bg-gray-100" iconColor="text-gray-600" label="Est. Production Cost" value={`$${(s?.total_production_cost || 0).toFixed(2)}`} />
        </div>
      )}

      {creditMemoSummary?.by_category?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Credits by Category</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {creditMemoSummary.by_category.map(cat => (
                <div key={cat.category} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">{cat.category_label}</span>
                  <Badge variant="secondary">{cat.count}</Badge>
                  <span className="text-sm font-medium text-red-600">${cat.total_credit.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center justify-between">
            <span>Credit Memo History</span>
            <Button size="sm" variant="outline" onClick={loadCreditMemos}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left p-3 font-medium text-gray-600">Credit Memo</th>
                  <th className="text-left p-3 font-medium text-gray-600">Invoice</th>
                  <th className="text-left p-3 font-medium text-gray-600">Client</th>
                  <th className="text-left p-3 font-medium text-gray-600">Category</th>
                  <th className="text-right p-3 font-medium text-gray-600">Credit</th>
                  <th className="text-left p-3 font-medium text-gray-600">Refund Status</th>
                  <th className="text-left p-3 font-medium text-gray-600">Date</th>
                  <th className="text-center p-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {creditMemos.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-8 text-gray-500">No credit memos found</td></tr>
                ) : creditMemos.map(cm => (
                  <tr key={cm.id} className="hover:bg-gray-50">
                    <td className="p-3 font-medium">{cm.credit_memo_number}</td>
                    <td className="p-3 text-gray-600">{cm.invoice_number}</td>
                    <td className="p-3">{cm.client_name || '—'}</td>
                    <td className="p-3"><Badge variant="outline">{cm.void_category_label}</Badge></td>
                    <td className="p-3 text-right font-medium text-red-600">${Math.abs(cm.credit_amount).toFixed(2)}</td>
                    <td className="p-3">
                      {cm.refund_status === 'pending_refund' ? (
                        <Badge className="bg-amber-100 text-amber-800">Pending Refund</Badge>
                      ) : cm.refund_status === 'refunded' ? (
                        <Badge className="bg-green-100 text-green-800">Refunded</Badge>
                      ) : (
                        <Badge variant="secondary">N/A</Badge>
                      )}
                    </td>
                    <td className="p-3 text-gray-500">{new Date(cm.created_at).toLocaleDateString()}</td>
                    <td className="p-3 text-center">
                      <Button size="sm" variant="ghost" onClick={() => onViewCreditMemo(cm)} data-testid={`view-credit-memo-${cm.id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </>
  );
};
